import type { ReactNode } from "react";

import type { DesignSystemElementProps, DesignSystemRef } from "@/design-system/contracts/dom";

export type ActivityTimelineProps = {
  children: ReactNode;
} & DesignSystemElementProps<"div"> &
  DesignSystemRef<HTMLDivElement>;

/** A compact vertical run of date-grouped activity. */
export function ActivityTimeline({ children, ref, ...nativeProps }: ActivityTimelineProps): ReactNode {
  return (
    <div {...nativeProps} ref={ref} className="relative flex flex-col gap-space-sm">
      {children}
    </div>
  );
}

export type ActivityTimelineGroupProps = {
  date: ReactNode;
  children: ReactNode;
} & DesignSystemElementProps<"div"> &
  DesignSystemRef<HTMLDivElement>;

/** One labelled day in an ActivityTimeline. */
export function ActivityTimelineGroup({ children, date, ref, ...nativeProps }: ActivityTimelineGroupProps): ReactNode {
  return (
    <div {...nativeProps} ref={ref}>
      <header className="relative my-space-md flex items-center justify-center text-caption whitespace-nowrap text-text-disabled">
        <span aria-hidden="true" className="absolute w-full max-w-measure-default border-b border-b-border-default" />
        <span className="relative bg-background-default px-space-md text-caption text-text-secondary">{date}</span>
      </header>
      {children}
    </div>
  );
}

export type ActivityTranslatedValueProps = DesignSystemElementProps<"span"> & DesignSystemRef<HTMLSpanElement>;

/** An emphasized value interpolated into translated activity copy. */
export function ActivityTranslatedValue({ ref, ...nativeProps }: ActivityTranslatedValueProps): ReactNode {
  return <span {...nativeProps} ref={ref} className="mx-space-xs text-body-emphasis not-italic" />;
}

export type ActivityStatusValueProps = DesignSystemElementProps<"span"> & DesignSystemRef<HTMLSpanElement>;

/** An inline status value interpolated into translated activity copy. */
export function ActivityStatusValue({ ref, ...nativeProps }: ActivityStatusValueProps): ReactNode {
  return <span {...nativeProps} ref={ref} className="mx-space-xs inline-block text-body-emphasis not-italic" />;
}
